import { useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import * as echarts from 'echarts'
import {
  SellPlanUnitPriceCurve,
  SellPlanUnitPricePoint,
} from '../../util/algorithm/sellPlanFeasibility'

interface Props {
  data: SellPlanUnitPriceCurve
  primaryLabel?: string
  comparisonData?: SellPlanUnitPriceCurve | null
  comparisonLabel?: string
  canAnalyze: boolean
  isLoading: boolean
  isStale: boolean
  onAnalyze: () => void
  width?: string
  height?: string
  showSummary?: boolean
  showAnalyzeButton?: boolean
}

interface WrapperProps {
  width?: string
}

const Wrapper = styled.div<WrapperProps>`
  display: flex;
  width: ${(props) => props.width || '100%'};
  padding: 16px;
  flex-direction: column;
  gap: 14px;
  border-radius: 8px;
  border: 1px solid #e6e6e6;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`

const ControlRow = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`

const ControlTextBox = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 4px;
`

const ControlTitle = styled.div`
  color: #2f3c5e;
  font-family: SUIT;
  font-size: 13px;
  font-weight: 700;
`

const ControlDescription = styled.div`
  color: #667085;
  font-family: SUIT;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.45;
`

const AnalyzeButton = styled.button`
  display: inline-flex;
  height: 34px;
  min-width: 132px;
  padding: 0 14px;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  background: #57c9a7;
  color: #1f2933;
  font-family: SUIT;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    opacity 0.2s ease;

  &:hover:not(:disabled) {
    background: #74e3c2;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`

const NoticeBox = styled.div<{ $warning?: boolean }>`
  display: flex;
  width: 100%;
  min-height: 72px;
  padding: 14px 16px;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  border: 1px solid
    ${(props) =>
      props.$warning ? 'rgba(208, 145, 24, 0.36)' : 'rgba(87, 201, 167, 0.35)'};
  background: ${(props) =>
    props.$warning ? 'rgba(255, 245, 214, 0.72)' : 'rgba(87, 201, 167, 0.08)'};
  color: #4d4d4d;
  font-family: SUIT;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.55;
  text-align: center;
`

const LoadingBox = styled.div`
  display: flex;
  width: 100%;
  min-height: 220px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  border-radius: 6px;
  background: rgba(87, 201, 167, 0.06);
  color: #2f3c5e;
  font-family: SUIT;
  font-size: 12px;
  font-weight: 700;
`

const Spinner = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 3px solid rgba(87, 201, 167, 0.24);
  border-top-color: #57c9a7;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`

const SummaryGrid = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 8px;
`

const SummaryItem = styled.div`
  display: flex;
  min-width: 0;
  padding: 10px 12px;
  flex-direction: column;
  gap: 4px;
  border-radius: 6px;
  border: 1px solid rgba(87, 201, 167, 0.35);
  background: rgba(87, 201, 167, 0.08);
`

const SummaryLabel = styled.div<{ $compact?: boolean }>`
  min-height: ${(props) => (props.$compact ? 'auto' : '30px')};
  color: #667085;
  font-family: SUIT;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.35;
`

const SummaryValue = styled.div<{ $color?: string; $emphasis?: boolean }>`
  color: ${(props) =>
    props.$color || (props.$emphasis ? '#147a5b' : '#2f3c5e')};
  font-family: SUIT;
  font-size: 15px;
  font-weight: 700;
  white-space: nowrap;
`

const SummarySubValue = styled.div<{ $emphasis?: boolean }>`
  color: ${(props) => (props.$emphasis ? '#147a5b' : '#667085')};
  font-family: SUIT;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.3;
  white-space: nowrap;
`

const SummarySuffix = styled.span`
  margin-left: 2px;
  font-family: SUIT;
  font-size: 11px;
  font-weight: 400;
`

const ChartBox = styled.div<{ height?: string }>`
  width: 100%;
  height: ${(props) => props.height || '320px'};
`

const formatWon = (value: number) => {
  if (Math.abs(value) >= 100000000) {
    return `${(value / 100000000).toFixed(1)}억`
  }

  if (Math.abs(value) >= 10000) {
    return `${(value / 10000).toFixed(0)}만`
  }

  return value.toLocaleString()
}

const formatWonDetail = (value: number) =>
  `${Number(value.toFixed(0)).toLocaleString()} 원`

const formatUnitNumber = (value: number | null | undefined) =>
  value === null || typeof value === 'undefined'
    ? '-'
    : value.toLocaleString(undefined, {
        maximumFractionDigits: 2,
      })

const formatUnit = (value: number | null | undefined) =>
  value === null || typeof value === 'undefined'
    ? '-'
    : `${formatUnitNumber(value)} 원/kWh`

const formatIntegerUnit = (value: number | null | undefined) =>
  value === null || typeof value === 'undefined'
    ? '-'
    : `${Math.floor(value).toLocaleString()} 원/kWh`

const formatPercent = (value: number | null | undefined) =>
  value === null || typeof value === 'undefined'
    ? '-'
    : `${value.toLocaleString(undefined, {
        maximumFractionDigits: 1,
      })}%`

const getClosestPoint = (
  data: SellPlanUnitPricePoint[],
  currentPurchaseAverageUnit: number
) =>
  data.reduce<SellPlanUnitPricePoint | null>((closest, point) => {
    if (!closest) return point

    return Math.abs(point.purchaseAverageUnit - currentPurchaseAverageUnit) <
      Math.abs(closest.purchaseAverageUnit - currentPurchaseAverageUnit)
      ? point
      : closest
  }, null)

const getProjectedProfitAtUnit = (
  sortedPoints: SellPlanUnitPricePoint[],
  unit: number
) => {
  if (sortedPoints.length === 0) return null
  if (sortedPoints.length === 1) return sortedPoints[0].totalProfit

  const exactPoint = sortedPoints.find(
    (point) => point.purchaseAverageUnit === unit
  )
  if (exactPoint) return exactPoint.totalProfit

  const getProjection = (
    left: SellPlanUnitPricePoint,
    right: SellPlanUnitPricePoint
  ) => {
    const slope =
      right.purchaseAverageUnit === left.purchaseAverageUnit
        ? 0
        : (right.totalProfit - left.totalProfit) /
          (right.purchaseAverageUnit - left.purchaseAverageUnit)

    return left.totalProfit + slope * (unit - left.purchaseAverageUnit)
  }

  if (unit < sortedPoints[0].purchaseAverageUnit) {
    return getProjection(sortedPoints[0], sortedPoints[1])
  }

  for (let index = 1; index < sortedPoints.length; index++) {
    const left = sortedPoints[index - 1]
    const right = sortedPoints[index]

    if (unit < right.purchaseAverageUnit) {
      return getProjection(left, right)
    }
  }

  return getProjection(
    sortedPoints[sortedPoints.length - 2],
    sortedPoints[sortedPoints.length - 1]
  )
}

const buildVisibleSeriesData = (
  points: SellPlanUnitPricePoint[],
  xMinUnit: number,
  xMaxUnit: number
) => {
  if (points.length === 0) return []

  const sortedPoints = [...points].sort(
    (left, right) => left.purchaseAverageUnit - right.purchaseAverageUnit
  )
  const minProfit = getProjectedProfitAtUnit(sortedPoints, xMinUnit)
  const maxProfit = getProjectedProfitAtUnit(sortedPoints, xMaxUnit)

  if (minProfit === null || maxProfit === null) {
    return []
  }

  const result = [
    [xMinUnit, minProfit],
    ...sortedPoints
      .filter(
        (point) =>
          point.purchaseAverageUnit > xMinUnit &&
          point.purchaseAverageUnit < xMaxUnit
      )
      .map((point) => [point.purchaseAverageUnit, point.totalProfit]),
    [xMaxUnit, maxProfit],
  ]

  return result
}

const buildBreakEvenPointData = (
  unitPriceLimit: number | null,
  xMinUnit: number,
  xMaxUnit: number
) =>
  unitPriceLimit === null ||
  unitPriceLimit < xMinUnit ||
  unitPriceLimit > xMaxUnit
    ? []
    : [[unitPriceLimit, 0]]

const getSeriesColor = (label: string) =>
  label.includes('최대부하 시간대') ? '#168C86' : '#2FBF8F'

const CURRENT_PURCHASE_COLOR = '#4F46E5'
const AXIS_TEXT_COLOR = '#344054'
const FEASIBLE_AREA_SERIES_NAME = '요금제 설계 가능 구간'
const FEASIBLE_AREA_COLOR = 'rgba(22, 140, 134, 0.12)'
const X_AXIS_MIN_UNIT = 100
const X_AXIS_MAX_UNIT = 250

const isVisibleUnit = (value: number | null | undefined) =>
  typeof value === 'number' &&
  value >= X_AXIS_MIN_UNIT &&
  value <= X_AXIS_MAX_UNIT

const buildFeasibleAreaData = (
  seriesData: number[][],
  unitPriceLimit: number | null,
  xMinUnit: number,
  xMaxUnit: number
) => {
  if (seriesData.length === 0 || unitPriceLimit === null) return []

  const endUnit = Math.min(unitPriceLimit, xMaxUnit)

  if (endUnit <= xMinUnit) return []

  const areaData = seriesData.filter(
    ([unit]) => unit >= xMinUnit && unit < endUnit
  )

  return [
    ...areaData,
    [
      endUnit,
      unitPriceLimit <= xMaxUnit
        ? 0
        : seriesData[seriesData.length - 1]?.[1] ?? 0,
    ],
  ]
}

const SellPlanFeasibilityChart = ({
  data,
  primaryLabel = '선택 요금제',
  comparisonData,
  comparisonLabel = '비교 요금제',
  canAnalyze,
  isLoading,
  isStale,
  onAnalyze,
  width,
  height,
  showSummary = true,
  showAnalyzeButton = true,
}: Props) => {
  const chartRef = useRef<echarts.ECharts | null>(null)
  const chartId = useRef(
    `sell-plan-unit-price-${Math.floor(Math.random() * 1000000)}`
  )
  const currentPoint = useMemo(
    () => getClosestPoint(data.points, data.currentPurchaseAverageUnit),
    [data.currentPurchaseAverageUnit, data.points]
  )
  const primaryColor = getSeriesColor(primaryLabel)
  const comparisonColor = getSeriesColor(comparisonLabel)

  useEffect(() => {
    const chartDom = document.getElementById(chartId.current)
    if (!chartDom || data.points.length === 0) return

    if (chartRef.current) {
      chartRef.current.dispose()
    }

    const chart = echarts.init(chartDom)
    const seriesData = buildVisibleSeriesData(
      data.points,
      X_AXIS_MIN_UNIT,
      X_AXIS_MAX_UNIT
    )
    const comparisonSeriesData = buildVisibleSeriesData(
      comparisonData?.points ?? [],
      X_AXIS_MIN_UNIT,
      X_AXIS_MAX_UNIT
    )
    const currentPointData = currentPoint
      ? [[data.currentPurchaseAverageUnit, data.currentTotalProfit]]
      : []
    const primaryBreakEvenPointData = buildBreakEvenPointData(
      data.unitPriceLimit,
      X_AXIS_MIN_UNIT,
      X_AXIS_MAX_UNIT
    )
    const feasibleAreaData = buildFeasibleAreaData(
      seriesData,
      data.unitPriceLimit,
      X_AXIS_MIN_UNIT,
      X_AXIS_MAX_UNIT
    )
    const comparisonUnitPriceLimit = comparisonData?.unitPriceLimit
    const comparisonBreakEvenPointData = buildBreakEvenPointData(
      comparisonUnitPriceLimit ?? null,
      X_AXIS_MIN_UNIT,
      X_AXIS_MAX_UNIT
    )
    const legendData =
      comparisonSeriesData.length > 0
        ? [primaryLabel, comparisonLabel, '현재 구매 평균단가']
        : [primaryLabel, '현재 구매 평균단가']

    const option: echarts.EChartsOption = {
      color: [primaryColor, comparisonColor, CURRENT_PURCHASE_COLOR],
      legend: {
        data: legendData,
        top: 34,
        left: 'center',
        textStyle: {
          fontSize: 11,
          color: '#667085',
          fontFamily: 'SUIT',
        },
      },
      title: {
        text: '구매 평균단가별 요금 설계 이익 분석',
        left: 'center',
        textStyle: {
          fontSize: 14,
          color: '#4d4d4d',
          fontFamily: 'SUIT',
          fontWeight: 700,
        },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          label: {
            backgroundColor: '#6a7985',
          },
        },
        formatter: (params: any) => {
          const hoveredPoint = params.find(
            (param: any) =>
              param.seriesType === 'line' &&
              param.seriesName !== FEASIBLE_AREA_SERIES_NAME
          )?.data as [number, number] | undefined
          const fallbackPoint = params.find(
            (param: any) => Array.isArray(param.data)
          )?.data as [number, number] | undefined
          const hoveredUnit =
            hoveredPoint?.[0] ??
            fallbackPoint?.[0] ??
            (typeof params[0]?.axisValue === 'number'
              ? params[0].axisValue
              : undefined)
          const profitLines = params
            .filter(
              (param: any) =>
                param.seriesType === 'line' &&
                param.seriesName !== FEASIBLE_AREA_SERIES_NAME
            )
            .map((param: any) => {
              const point = param.data as [number, number] | undefined
              return `${param.marker}${param.seriesName} 요금 설계 이익: <b>${formatWonDetail(
                Math.floor(point?.[1] ?? 0)
              )}</b>`
            })

          return [
            `단가: <b>${formatIntegerUnit(hoveredUnit)}</b>`,
            ...profitLines,
            `현재 구매 평균단가: <b>${formatUnit(
              data.currentPurchaseAverageUnit
            )}</b>`,
            `요금제 설계 가능 단가: <b>${formatUnit(data.unitPriceLimit)}</b>`,
          ].join('<br/>')
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '10%',
        top: 96,
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        min: X_AXIS_MIN_UNIT,
        max: X_AXIS_MAX_UNIT,
        name: 'ESS 전력 구매 평균단가(원/kWh)',
        nameLocation: 'middle',
        nameGap: 28,
        nameTextStyle: {
          color: AXIS_TEXT_COLOR,
          fontFamily: 'SUIT',
          fontWeight: 400,
        },
        axisLine: {
          lineStyle: {
            color: '#8a8f98',
          },
        },
        axisLabel: {
          color: AXIS_TEXT_COLOR,
          fontFamily: 'SUIT',
          fontWeight: 400,
          formatter: (value: number) =>
            value.toLocaleString(undefined, {
              maximumFractionDigits: 0,
            }),
        },
      },
      yAxis: {
        type: 'value',
        name: '요금 설계 이익(원)',
        nameTextStyle: {
          color: AXIS_TEXT_COLOR,
          fontFamily: 'SUIT',
          fontWeight: 400,
        },
        axisLabel: {
          color: AXIS_TEXT_COLOR,
          fontFamily: 'SUIT',
          fontWeight: 400,
          formatter: (value: number) => formatWon(value),
        },
        splitLine: {
          lineStyle: {
            color: '#e6e6e6',
            type: 'dashed',
          },
        },
      },
      series: [
        ...(feasibleAreaData.length === 0
          ? []
          : [
              {
                name: FEASIBLE_AREA_SERIES_NAME,
                type: 'line' as const,
                smooth: true,
                showSymbol: false,
                data: feasibleAreaData,
                lineStyle: {
                  opacity: 0,
                },
                itemStyle: {
                  color: FEASIBLE_AREA_COLOR,
                },
                areaStyle: {
                  color: FEASIBLE_AREA_COLOR,
                  origin: 0,
                },
                silent: true,
                z: 1,
                animation: false,
              },
            ]),
        {
          name: primaryLabel,
          type: 'line',
          smooth: true,
          showSymbol: false,
          symbol: 'circle',
          symbolSize: 7,
          data: seriesData,
          lineStyle: {
            width: 3,
            color: primaryColor,
          },
          itemStyle: {
            color: primaryColor,
          },
          z: 3,
          animation: false,
        },
        ...(comparisonSeriesData.length === 0
          ? []
          : [
              {
                name: comparisonLabel,
                type: 'line' as const,
                smooth: true,
                showSymbol: false,
                symbol: 'circle',
                symbolSize: 7,
                data: comparisonSeriesData,
                lineStyle: {
                  width: 2.5,
                  color: comparisonColor,
                },
                itemStyle: {
                  color: comparisonColor,
                },
                z: 3,
                animation: false,
              },
            ]),
        {
          name: `${primaryLabel} 설계 가능 단가`,
          type: 'scatter',
          data: primaryBreakEvenPointData,
          symbolSize: 11,
          itemStyle: {
            color: primaryColor,
            borderColor: '#ffffff',
            borderWidth: 2,
          },
          z: 5,
          animation: false,
        },
        ...(comparisonBreakEvenPointData.length === 0
          ? []
          : [
              {
                name: `${comparisonLabel} 설계 가능 단가`,
                type: 'scatter' as const,
                data: comparisonBreakEvenPointData,
                symbolSize: 11,
                itemStyle: {
                  color: comparisonColor,
                  borderColor: '#ffffff',
                  borderWidth: 2,
                },
                z: 5,
                animation: false,
              },
            ]),
        {
          name: '현재 구매 평균단가',
          type: 'scatter',
          data: currentPointData,
          symbolSize: 11,
          itemStyle: {
            color: CURRENT_PURCHASE_COLOR,
            borderColor: '#ffffff',
            borderWidth: 2,
          },
          markLine: {
            symbol: ['none', 'none'],
            label: {
              show: false,
            },
            lineStyle: {
              color: CURRENT_PURCHASE_COLOR,
              type: 'dashed',
              width: 1.5,
            },
            data: !isVisibleUnit(data.currentPurchaseAverageUnit)
              ? []
              : [
                  {
                    xAxis: data.currentPurchaseAverageUnit,
                    name: 'current-purchase-average-unit',
                  },
                ],
          },
          z: 4,
          animation: false,
        },
      ],
    }

    const handleResize = () => {
      chart.resize()
    }

    window.addEventListener('resize', handleResize)
    chart.setOption(option)
    chartRef.current = chart

    return () => {
      chart.dispose()
      window.removeEventListener('resize', handleResize)
    }
  }, [
    comparisonData,
    comparisonLabel,
    currentPoint,
    data,
    primaryColor,
    primaryLabel,
    comparisonColor,
  ])

  return (
    <Wrapper width={width}>
      <ControlRow>
        <ControlTextBox>
          <ControlTitle>전력 구매 평균 단가별 요금 설계 이익 분석</ControlTitle>
          <ControlDescription>
            현재 설계 조건 기준으로 분석합니다.
            <br />
            x축은 ESS 전력 구매 평균단가, y축은 요금 설계 이익입니다.
            <br />
            요금 설계 이익이 0원을 넘는 구간에서 요금제 설계가 가능합니다.
          </ControlDescription>
        </ControlTextBox>
        {showAnalyzeButton ? (
          <AnalyzeButton
            type="button"
            disabled={!canAnalyze || isLoading}
            onClick={onAnalyze}
          >
            {isLoading ? '분석 중' : '설계 가능 단가 분석'}
          </AnalyzeButton>
        ) : null}
      </ControlRow>
      {!canAnalyze ? (
        <NoticeBox>
          분석 대기: 수용가 부하, 구매요금제, 한전요금제, 공휴일 등 필수 조건
          입력 후 실행 가능
        </NoticeBox>
      ) : isLoading ? (
        <LoadingBox>
          <Spinner />
          현재 설계 조건 기준 설계 가능 단가 계산 중
        </LoadingBox>
      ) : data.points.length === 0 ? (
        <NoticeBox>
          설계 가능 단가 확인 필요
          <br />
          버튼을 눌러 현재 입력 조건으로 분석을 실행하세요.
        </NoticeBox>
      ) : (
        <>
          {isStale ? (
            <NoticeBox $warning>
              입력 조건 변경 감지
              <br />
              최신 결과 확인을 위해 설계 가능 단가 분석을 다시 실행하세요.
            </NoticeBox>
          ) : null}
          {showSummary ? (
            <SummaryGrid>
              <SummaryItem>
                <SummaryLabel>
                  {primaryLabel}
                  <br />
                  설계 가능 단가
                </SummaryLabel>
                <SummaryValue $emphasis>
                  {formatUnit(data.unitPriceLimit)}
                  {data.unitPriceLimit === null ? null : (
                    <SummarySuffix>이하</SummarySuffix>
                  )}
                </SummaryValue>
              </SummaryItem>
              {comparisonData ? (
                <SummaryItem>
                  <SummaryLabel>
                    {comparisonLabel}
                    <br />
                    설계 가능 단가
                  </SummaryLabel>
                  <SummaryValue $emphasis>
                    {formatUnit(comparisonData.unitPriceLimit)}
                    {comparisonData.unitPriceLimit === null ? null : (
                      <SummarySuffix>이하</SummarySuffix>
                    )}
                  </SummaryValue>
                </SummaryItem>
              ) : null}
              <SummaryItem>
                <SummaryLabel>현재 구매 평균단가</SummaryLabel>
                <SummaryValue $color={CURRENT_PURCHASE_COLOR}>
                  {formatUnit(data.currentPurchaseAverageUnit)}
                </SummaryValue>
              </SummaryItem>
            </SummaryGrid>
          ) : null}
          <ChartBox id={chartId.current} height={height} />
        </>
      )}
    </Wrapper>
  )
}

export default SellPlanFeasibilityChart
